import {sendAnalyticsEvent} from '#/analytics/analytics';
import {SuccessIndicator} from '#/atoms/dots-loading-indicator';
import {Modal} from '#/atoms/modal';
import {EmailUsLink} from '#/components/links/social-links';
import {ModalContext} from '#/contexts/modal-provider';
import {WalletContext} from '#/contexts/wallet-provider';
import {userFeedbackRequests} from '#/repository/fetch-user-feedback';
import {StarIcon} from '@heroicons/react/24/solid';
import {useMutation} from '@tanstack/react-query';
import {FormEvent, FunctionComponent, useContext, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {useLocation} from 'react-router-dom';
import {ApiUserFeedback} from 'shareland-common';

const maxCharacterCount = 300;

type Rating = ApiUserFeedback['generalCreate']['reqBody']['rating'];
const availableRatings: Rating[] = [1, 2, 3, 4, 5];

export const UserFeedbackModal: FunctionComponent = () => {
  const {t} = useTranslation('translation');

  // Contexts
  const {close} = useContext(ModalContext);

  // External hooks
  const location = useLocation();
  const {
    accountResult: {address: account},
  } = useContext(WalletContext);

  // Internal hooks
  const userFeedbackMutation = useMutation({
    mutationKey: ['user-feedback-mutation'],
    mutationFn: async () => {
      const userFeedbackResult = await userFeedbackRequests.generalCreate({
        account,
        rating: selectedRating as Rating,
        comment: commentInput,
        url: location.pathname + location.search,
      });

      return userFeedbackResult;
    },
  });

  const [selectedRating, setSelectedRating] = useState<number>();
  const [commentInput, setCommentInput] = useState('');

  // Vars
  const isLoading = userFeedbackMutation.isPending;
  const canSendFeedback = selectedRating !== undefined && commentInput.length <= maxCharacterCount;
  const isSubmitButtonDisabled = !canSendFeedback || isLoading;

  // Functions
  const onTextAreaChange = (event: FormEvent<HTMLTextAreaElement>) => {
    setCommentInput(event.currentTarget.value);
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!canSendFeedback || isLoading) {
      return;
    }

    sendAnalyticsEvent.feedbackSubmitted();
    await userFeedbackMutation.mutateAsync();
  };

  return (
    <Modal title={t('pages.userFeedback.title')} onClose={close}>
      <form className='p-6 flex flex-col items-center gap-2 min-w-[340px] relative' onSubmit={onSubmit}>
        <div className={'flex'}>
          {availableRatings.map(rating => (
            <RatingButton
              key={`rating-button-${rating}`}
              rating={rating}
              selectedRating={selectedRating}
              setSelectedRating={setSelectedRating}
            />
          ))}
        </div>

        <div className='w-full flex flex-col bg-gray-100 mt-2 pb-6 rounded relative focus-within:outline focus-within:outline-2 outline-deepblue'>
          <textarea
            rows={3}
            className='w-full bg-transparent p-4 resize-none text-black/90 outline-none'
            disabled={isLoading}
            value={commentInput}
            onChange={onTextAreaChange}
            placeholder={t('pages.userFeedback.textPlaceholder')}
          />
          <div className='p-1 text-xs text-black/20 absolute right-0 bottom-0'>
            {t('pages.userFeedback.textCharacterCount', {
              count: commentInput.length,
              maxCount: maxCharacterCount,
            })}
          </div>
        </div>

        <button
          type='submit'
          className={
            'w-full mt-2 px-4 py-2 text-white rounded flex-center bg-deepblue data-[error=true]:bg-downred data-[loading=true]:bg-gray-400 hover:opacity-70 disabled:opacity-50 transition-all duration-200 ease-in-out'
          }
          data-loading={isLoading}
          data-error={!!userFeedbackMutation.error}
          disabled={isSubmitButtonDisabled}
        >
          {isLoading
            ? t('statuses.sending')
            : userFeedbackMutation.error
            ? t('actions.errorTryAgain')
            : t('actions.submit')}
        </button>

        <div className='w-full flex items-center text-sm gap-1 mt-2'>
          <div className='grow h-px bg-gray-200' />
          <span className='text-gray-200 uppercase'>{t('basics.or')}</span>
          <div className='grow h-px bg-gray-200' />
        </div>

        <div className='w-full text-gray-400 text-sm'>
          <Trans
            i18nKey='pages.userFeedback.generalEnquiriesCta'
            components={{
              CTAEmailUs: <EmailUsLink className='text-deepblue inline-flex' />,
            }}
          />
        </div>

        {userFeedbackMutation.status === 'success' && (
          <div className='w-full h-full absolute top-0 left-0 bg-white flex-center'>
            <SuccessIndicator>{t('pages.userFeedback.feedbackSent')}</SuccessIndicator>
          </div>
        )}
      </form>
    </Modal>
  );
};

const RatingButton: FunctionComponent<{
  rating: number;
  selectedRating: number | undefined;
  setSelectedRating: React.Dispatch<React.SetStateAction<number | undefined>>;
}> = ({rating, selectedRating, setSelectedRating}) => {
  const showAsSelected = selectedRating && selectedRating >= rating;

  const onClick = () => {
    setSelectedRating(rating);
  };

  return (
    <button type='button' className='p-2 hover:opacity-70' onClick={onClick}>
      <StarIcon
        className={`w-8 aspect-square ${
          showAsSelected ? 'fill-yellow-500 stroke-yellow-500' : 'fill-gray-300 stroke-gray-300'
        }`}
      />
    </button>
  );
};
